import { useEffect, useState } from 'react';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { useStorage } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { LANGUAGES } from '../../../constants';
import type { Language, LanguageCode } from '../../../types';
import { checkLanguageExists } from '../../../utils/check-language-exists';
import type { StyleProps } from '../../styles/types';
import { getSiteLanguage } from '../../utils/get-site-language';
import { Dropdown } from './dropdown';
import type { DropdownAnchorItemProps } from './dropdown-anchor-item';
import { DropdownAnchorItem } from './dropdown-anchor-item';
import { DropdownItem } from './dropdown-item';

type DropdownLayoutProps = StyleProps & {
  activeItemNameKey?: keyof Pick<
    Language,
    'name' | 'codeName' | 'originalName'
  >;
  withFlag?: boolean;
  flagStyle?: DropdownAnchorItemProps['iconStyle'];
  activeItemIconName?: DropdownAnchorItemProps['iconName'];
};

export function DropdownLayout({
  items,
  activeItemNameKey,
  withFlag = false,
  flagStyle,
  activeItemIconName,
  onClick
}: DropdownLayoutProps) {
  const { editMode, languages, googleAnalytics, widgetId } = useSettings();
  const [storedLanguage] = useStorage<LanguageCode | undefined>(
    'language',
    undefined
  );

  const [open, setOpen] = useState(false);
  const [anchorElement, setAnchorElement] = useState<Element | null>(null);
  const [activeLanguageCode, setActiveLanguageCode] = useState<LanguageCode>(
    items[0].language
  );

  const closeRequest = () => setOpen(false);

  const activeLanguage = LANGUAGES[activeLanguageCode];

  useEffect(() => {
    if (editMode) {
      const activeItem = items.find(
        ({ language }) => language === activeLanguageCode
      );

      if (!activeItem) {
        setActiveLanguageCode(items[0].language);
      }

      return;
    }

    if (storedLanguage) {
      const storedItem = items.find(
        ({ language }) => language === storedLanguage
      );

      if (storedItem) {
        setActiveLanguageCode(storedLanguage);
        return;
      }
    }

    const siteLang = getSiteLanguage(languages);
    const isSiteLanguageExitsInApp =
      !!siteLang && checkLanguageExists(siteLang);

    if (siteLang) {
      const siteLangItem = items.find(({ language }) => language === siteLang);

      if (siteLangItem && isSiteLanguageExitsInApp) {
        setActiveLanguageCode(siteLang);
        return;
      }
    }

    setActiveLanguageCode(items[0].language);
  }, [editMode, items, storedLanguage, languages, activeLanguageCode]);

  useEffect(() => {
    if (!open) {
      return;
    }

    sendGoogleAnalyticsEvents({
      eventType: 'widgetOpened',
      googleAnalyticsEvents: googleAnalytics,
      editMode,
      valueMapping: {
        widgetId,
        languageCode: activeLanguageCode,
        languageName: activeLanguage?.name || activeLanguageCode
      }
    });
  }, [
    open,
    googleAnalytics,
    widgetId,
    editMode,
    activeLanguageCode,
    activeLanguage
  ]);

  return (
    <>
      <DropdownAnchorItem
        ref={setAnchorElement}
        languageCode={activeLanguageCode}
        name={activeItemNameKey ? activeLanguage[activeItemNameKey] : undefined}
        icon={withFlag ? activeLanguage.flag : undefined}
        iconStyle={flagStyle}
        iconName={activeItemIconName}
        isOpen={open}
        setOpenRequest={() => setOpen(true)}
      />

      <Dropdown
        anchorElement={anchorElement}
        open={open}
        onCloseRequest={closeRequest}
      >
        {items?.map(({ id, language }) => {
          const { flag, originalName } = LANGUAGES[language];

          return (
            <DropdownItem
              key={id}
              name={originalName}
              ariaLabel={`${originalName} (${language})`}
              selected={language === activeLanguageCode}
              icon={withFlag ? flag : undefined}
              iconStyle={flagStyle}
              onClick={() => {
                closeRequest();

                if (language === activeLanguageCode) {
                  return;
                }

                setActiveLanguageCode(language);
                onClick(language);
              }}
            />
          );
        })}
      </Dropdown>
    </>
  );
}
